import { Link } from "react-router-dom";
import { ShieldCheck, Zap, Globe, Lock, Server, LifeBuoy, Palette, ArrowRight, Check, Star } from "lucide-react";
import { PLANS, SERVICES } from "@/lib/content";
import SEO from "@/components/SEO";
import PayPalButton from "@/components/PayPalButton";
import TestimonialCarousel from "@/components/TestimonialCarousel";

const iconMap = { ShieldCheck, Lock, Server, Globe, Palette, LifeBuoy };

export default function Home() {
  const popular = PLANS.find((p) => p.popular);

  return (
    <>
      <SEO
        title="Tech Alpha SSL — Enterprise SSL, Security & Hosting"
        description="Premium SSL certificates, deep security audits, and secure hosting for modern businesses. A+ SSL Labs. Trusted by fintech, healthcare, and SaaS."
        canonical="https://techalphassl.com/"
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-mesh-hero opacity-70 dark:opacity-60" />
        <div className="absolute inset-0 -z-10 grid-lines opacity-40" />
        <div className="container-page pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="max-w-3xl">
            <span className="eyebrow" data-testid="hero-eyebrow">
              <ShieldCheck className="h-3.5 w-3.5" /> Trusted SSL & Security
            </span>
            <h1 className="mt-5 text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[0.95]">
              Security you can{" "}
              <span className="bg-gradient-to-r from-[#1565FF] to-[#4FC3F7] bg-clip-text text-transparent">
                actually trust.
              </span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              Enterprise-grade SSL certificates, deep security audits, and battle-tested
              hosting — engineered by senior engineers, delivered with SLA-backed support.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/pricing" className="btn-primary" data-testid="hero-cta-pricing">
                See Pricing <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-ghost" data-testid="hero-cta-quote">
                Request a Quote
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-[#1565FF]" /> A+ SSL Labs grade</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-[#1565FF]" /> 24/7 monitoring</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-[#1565FF]" /> Zero-downtime migrations</div>
            </div>
          </div>

          {/* Floating glass cards */}
          <div className="mt-16 grid gap-4 md:grid-cols-3">
            {[
              { icon: ShieldCheck, kpi: "99.99%", label: "Uptime SLA" },
              { icon: Zap, kpi: "<24h", label: "SSL install time" },
              { icon: Lock, kpi: "A+", label: "SSL Labs grade" },
            ].map((it, i) => (
              <div key={i} className="glass rounded-2xl p-6 flex items-center gap-4">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#1565FF]/10 text-[#1565FF] dark:bg-white/5 dark:text-[#4FC3F7]">
                  <it.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-2xl font-semibold tracking-tight">{it.kpi}</div>
                  <div className="text-sm text-muted-foreground">{it.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 md:py-28">
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="eyebrow">Services</span>
            <h2 className="mt-4 section-title">Everything you need to run securely.</h2>
            <p className="mt-3 text-muted-foreground text-lg">
              From SSL to hosting to a full security posture — one team, six services, zero handoffs.
            </p>
          </div>

          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => {
              const Icon = iconMap[s.icon] || ShieldCheck;
              return (
                <div key={s.id} className="card-elevated p-8 hover:-translate-y-1 transition-transform group" data-testid={`service-card-${s.id}`}>
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#1565FF]/10 text-[#1565FF] dark:bg-white/5 dark:text-[#4FC3F7] group-hover:scale-110 transition-transform">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold tracking-tight">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.description}</p>
                  <Link to="/services" className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#1565FF] dark:text-[#4FC3F7]">
                    Learn more <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PLAN */}
      {popular && (
        <section className="py-20 md:py-28 bg-gradient-to-br from-[#0B1220] to-[#0d1a35] text-white">
          <div className="container-page grid gap-10 md:grid-cols-2 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs">
                <Star className="h-3 w-3 fill-current" /> Most Popular
              </span>
              <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tighter">
                {popular.name}
              </h2>
              <p className="mt-3 text-white/70 text-lg max-w-lg">{popular.description}</p>
              <div className="mt-6 flex items-baseline gap-2">
                <div className="text-5xl font-semibold tracking-tighter">{popular.priceLabel}</div>
                <div className="text-white/60">one-time</div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <PayPalButton
                  plan_id={popular.id}
                  plan_name={popular.name}
                  price={popular.price}
                  label={popular.ctaLabel}
                  testId="home-featured-buy"
                />
                <Link to="/pricing" className="btn-ghost !bg-white/10 !text-white !border-white/20 hover:!bg-white/20" data-testid="home-featured-compare">Compare plans</Link>
              </div>
            </div>
            <ul className="grid gap-3">
              {popular.features.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 grid h-5 w-5 place-items-center rounded-full bg-[#4FC3F7]/20 text-[#4FC3F7]"><Check className="h-3 w-3"/></span>
                  <span className="text-white/85">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* TRUST STRIP */}
      <section className="py-20">
        <div className="container-page">
          <div className="grid gap-8 md:grid-cols-4">
            {[
              { kpi: "500+", label: "Sites secured" },
              { kpi: "12+", label: "Countries served" },
              { kpi: "A+", label: "SSL Labs grade" },
              { kpi: "<1h", label: "Response time" },
            ].map((it, i) => (
              <div key={i} className="text-center md:text-left">
                <div className="text-4xl font-semibold tracking-tighter text-[#1565FF] dark:text-[#4FC3F7]">{it.kpi}</div>
                <div className="mt-1 text-sm text-muted-foreground">{it.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TestimonialCarousel featured="home" />

      {/* CTA */}
      <section className="py-20 md:py-28">
        <div className="container-page">
          <div className="card-elevated p-10 md:p-16 text-center overflow-hidden relative">
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#1565FF]/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-[#4FC3F7]/10 blur-3xl" />
            <h2 className="relative text-3xl md:text-5xl font-semibold tracking-tighter">Ready to secure your business?</h2>
            <p className="relative mt-4 text-muted-foreground max-w-xl mx-auto">
              Talk to a senior engineer today. Get a tailored plan, timeline, and quote — no sales fluff.
            </p>
            <div className="relative mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/quote" className="btn-primary" data-testid="home-cta-quote">Get a Quote</Link>
              <Link to="/contact" className="btn-ghost" data-testid="home-cta-contact">Contact Sales</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
